import { ApolloError } from "@apollo/client";
import {
  useAllUserDocumentsByTypeQuery,
  useDeleteUserDocumentMutation,
  useExhibitFilesQuery,
  useUnlinkExhibitFileMutation,
} from "@codegen/index";
import { IndividualDocumentType } from "@codegen/schema";
import { Button } from "@components/button";
import { Input } from "@components/input";
import { FormContainer, OnboardingContainer } from "@components/onboarding";
import { useReadOnlyMode } from "@components/readOnlyModeProvider";
import { UploadFileStatus } from "@components/uploader/types";
import Uploader from "@components/uploader/uploader";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  FileFootnote,
  ScreenshotFootnote,
} from "@pages/onboarding/shared/footnotes";
import { OnboardingPageType } from "@pages/onboarding/types";
import {
  CriticalRoleInternalDocuments,
  CriticalRolePatents,
  CriticalRoleScholarlyArticle,
} from "@pages/onboarding/workflows/descriptions";
import { X } from "@phosphor-icons/react";
import { useDebounceCallback } from "@react-hook/debounce";
import * as Sentry from "@sentry/react";
import { api } from "@utils/api";
import { useLogError } from "@utils/error";
import { useJwt } from "@utils/hooks";
import * as changeCase from "change-case";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
  useWatch,
} from "react-hook-form";
import toast from "react-hot-toast";
import { z } from "zod";

export const fileFormSchema = z.union([
  z.object({
    links: z.string().url().array().min(1, "Please provide a valid URL"),
  }),
  z.object({
    files: z.string().array().min(1, "Please upload at least one file"),
  }),
]);

export type FileFormType = z.infer<typeof fileFormSchema>;
export const FileWithLinksForm: React.FC<
  OnboardingPageType & {
    fileType: IndividualDocumentType;
    title: string;
    subtitle: React.ReactNode;
    description: React.ReactNode;
  }
> = ({
  contact,
  onBack,
  onSubmit,
  autoSave,
  onSkip,
  isSaving,
  fileType,
  title,
  subtitle,
  description,
  footnote,
  exhibit,
  onSkipForNow,
  moduleProgress,
  currentStep,
  showSkipForNow,
  hideExitButton,
}) => {
  const { required, data } = exhibit;
  const { links }: { links?: string[]; files?: string[] } =
    (data as FileFormType) ?? {
      links: [""],
      files: [],
    };

  const [status, setStatus] = useState<
    "idle" | "loading" | "error" | "success"
  >("idle");
  const { isReadOnly } = useReadOnlyMode();
  const token = useJwt();
  const logError = useLogError();
  const [error, setError] = useState<string | null>(null);

  // Track pending save operations to avoid race conditions
  const pendingSaveRef = useRef<Promise<void> | null>(null);

  const {
    data: userData,
    loading,
    refetch,
  } = useAllUserDocumentsByTypeQuery({
    variables: {
      userId: contact.id,
      documentType: fileType,
    },
  });

  const currentFiles = useMemo(() => {
    return (
      userData?.allUserDocuments?.nodes
        ?.map((node) => node?.fileByFileId)
        .filter((n) => !!n)
        .map((x) => ({
          id: x.id ?? "",
          name: x.name ?? "",
        })) ?? []
    );
  }, [userData]);

  // Query exhibit files
  const { data: exhibitFiles } = useExhibitFilesQuery({
    variables: {
      id: exhibit.exhibitId?.toString() ?? "",
    },
  });

  // Mutations for file operations
  const [unlinkExhibitFile] = useUnlinkExhibitFileMutation();
  const [deleteUserDocument] = useDeleteUserDocumentMutation();

  const methods = useForm<FileFormType>({
    defaultValues: {
      links: links ?? [""],
      files: currentFiles?.map((f) => f.id) ?? [],
    },
    resolver: zodResolver(fileFormSchema),
  });

  const { getValues, setValue, control, formState } = methods;

  const watchedValues = useWatch({
    control,
  });

  const handleSubmit = useCallback(
    async (data?: FileFormType) => {
      try {
        // Wait for any pending auto-save to complete
        if (pendingSaveRef.current) {
          await pendingSaveRef.current;
        }

        setError(null);
        setStatus("loading");
        onSubmit(data);
        setStatus("success");
      } catch (e) {
        setStatus("error");
        if (e instanceof Error) {
          setError(e.message);
        }
        logError(e, { message: "Error submitting links and files" });
      }
    },
    [onSubmit, logError]
  );

  const handleSave = useCallback(
    async (data?: FileFormType) => {
      const savePromise = (async () => {
        try {
          setStatus("loading");
          await autoSave(data);
          await refetch();
          setStatus("success");
        } catch (e) {
          setStatus("error");
          logError(e, { message: "Error saving links and files" });
        } finally {
          pendingSaveRef.current = null;
        }
      })();

      pendingSaveRef.current = savePromise;
      await savePromise;
    },
    [autoSave, logError, refetch]
  );

  const debouncedSave = useDebounceCallback(handleSave, 300);
  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      debouncedSave(watchedValues as FileFormType);
    }
  }, [watchedValues, debouncedSave]);

  // Delete file handler
  const handleDelete = useCallback(
    async (file: UploadFileStatus) => {
      try {
        setError(null);
        setStatus("loading");
        const exhibitFile =
          exhibitFiles?.exhibitById?.exhibitFilesByExhibitId?.nodes?.find(
            (f) => f?.fileByFileId?.id === file.id
          );

        // Unlink from exhibit if it exists
        if (exhibit.exhibitId && exhibitFile) {
          Sentry.addBreadcrumb({
            category: "exhibit",
            message: `Unlinking exhibit file ${file.id} from exhibit ${exhibit.exhibitId}`,
          });

          await unlinkExhibitFile({
            variables: {
              input: {
                clientMutationId: `unlink-exhibit-file-${exhibit.exhibitId}-${file.id}`,
                exhibitId: exhibit.exhibitId.toString(),
                fileId: file.id,
              },
            },
          });
        }

        // Delete the file
        if (file.id) {
          try {
            const { errors } = await deleteUserDocument({
              variables: {
                input: {
                  clientMutationId: `delete-user-document-${contact.id}-${file.id}`,
                  userId: contact.id,
                  fileId: file.id,
                },
              },
            });

            if (errors != null) {
              throw new Error(errors[0].message);
            }
          } catch (e) {
            if (e instanceof Error) {
              logError(e, { contactId: contact.id, fileId: file.id });
            } else if (e instanceof ApolloError) {
              logError(e.graphQLErrors[0], {
                contactId: contact.id,
                fileId: file.id,
              });
            } else {
              logError(e, { contactId: contact.id, fileId: file.id });
            }
            throw e;
          }
        }

        // Update files in form
        const files = getValues("files");
        setValue(
          "files",
          files?.filter((id) => id.toString() !== file.id.toString()) ?? []
        );
        await refetch();
        setStatus("success");
      } catch (e) {
        setStatus("error");
        logError(e, { contactId: contact.id, fileId: file.id });
        toast.error("An error occurred while deleting the file");
      }
    },
    [
      contact.id,
      exhibit.exhibitId,
      exhibitFiles,
      unlinkExhibitFile,
      deleteUserDocument,
      getValues,
      setValue,
      logError,
      refetch,
    ]
  );

  const onFileUpload = useCallback(
    async (file: File) => {
      try {
        setStatus("loading");
        setError(null);
        const data = await api.files.uploadIndividualDoc(
          file,
          token,
          contact.id,
          fileType
        );

        if (!data.success || !data.id)
          throw new Error("An error occurred while uploading the file");

        const { id } = data;
        const files = getValues("files") ?? [];
        setValue("files", [...files, id]);

        const newFile = {
          id,
          name: file.name,
          success: data.success,
          fileId: id,
        };

        await refetch();
        await handleSave({ files: [...files, id] });
        setStatus("success");

        return {
          success: data.success,
          name: file.name,
          id,
          link: "",
          fileId: id,
        };
      } catch (exception) {
        setStatus("error");
        logError(exception, {
          contactId: contact.id,
          fileType,
        });
        return {
          success: false,
          name: file.name,
          type: file.type,
          link: "",
          id: "",
        };
      }
    },
    [
      contact.id,
      token,
      fileType,
      getValues,
      setValue,
      logError,
      refetch,
      handleSave,
    ]
  );

  const { fields, append, remove } = useFieldArray({
    control,
    name: "links",
  });

  useEffect(() => {
    if (fields.length < 1) {
      append("");
    }
  }, [fields, append]);

  return (
    <FormProvider {...methods}>
      <OnboardingContainer
        title={title}
        subtitle={subtitle}
        description={description}
        onSubmit={methods.handleSubmit(handleSubmit)}
        exhibit={exhibit}
        name={`${changeCase.kebabCase(fileType)}-form`}
        footnote={footnote}
        onBack={onBack}
        onSkipForNow={required ? onSkipForNow : onSkip}
        isLoading={isSaving || status === "loading"}
        optional={!required}
        isSaving={isSaving}
        progress={moduleProgress}
        currentStep={currentStep}
        showSkipForNow={showSkipForNow}
        hideExitButton={hideExitButton}
      >
        <FormContainer
          status={status}
          errorMessage={
            error ||
            "There was a problem uploading your files or saving your links. Please try again."
          }
          successMessage={
            status === "success" ? "Files uploaded successfully." : undefined
          }
        >
          <div className="space-y-6">
            <div className="">
              <p className="mb-1">
                {changeCase.sentenceCase(fileType)}&nbsp;links
              </p>
              <div className="relative flex flex-col gap-3">
                {fields.map((field, index) => (
                  <div
                    key={field.id}
                    className="w-full h-fit p-4 bg-[#F7FCFF] rounded-[16px] border-[3px] border-dashed border-[#B7D3E8]"
                  >
                    <div>
                      <label
                        htmlFor={`links.${index}`}
                        className="mb-1 text-sm font-semibold text-[#4C4F52] leading-5 leading-trim-both text-edge-cap block"
                      >
                        Link {index + 1}
                      </label>
                      <Controller
                        name={`links.${index}`}
                        control={control}
                        render={({ field }) => (
                          <Input
                            {...field}
                            id={`links.${index}`}
                            disabled={isReadOnly}
                            className="w-full"
                            type="text"
                            placeholder="https://www.google.com"
                          />
                        )}
                      />
                      {formState.errors.links?.[index]?.message && (
                        <p className="text-sm text-red-500 mt-1">
                          {formState.errors.links?.[index]?.message}
                        </p>
                      )}
                    </div>
                    <button
                      type="button"
                      disabled={isReadOnly}
                      onClick={() => remove(index)}
                      className="absolute top-0 right-0 p-2"
                    >
                      <X />
                    </button>
                  </div>
                ))}
              </div>
              {formState.isValid && (
                <Button
                  disabled={isReadOnly}
                  type="button"
                  variant={"link"}
                  className="self-start text-accent px-0 mt-2"
                  onClick={() => append("")}
                >
                  Add another link
                </Button>
              )}
            </div>

            <div>
              <p className="mb-1">{changeCase.sentenceCase(fileType)} files</p>
              {loading ? (
                <div className="flex justify-center items-center py-4">
                  <div className="animate-spin h-6 w-6 border-2 border-accent rounded-full border-t-transparent"></div>
                </div>
              ) : (
                <Uploader
                  multiple
                  onFileUpload={onFileUpload}
                  defaultFiles={currentFiles}
                  disabled={isReadOnly}
                  allowDelete
                  onDeleteFile={handleDelete}
                />
              )}
              {formState.errors.files?.message && (
                <p className="text-sm text-red-500 mt-1">
                  {formState.errors.files.message}
                </p>
              )}
            </div>
          </div>
        </FormContainer>
      </OnboardingContainer>
    </FormProvider>
  );
};

export const PatentForm: React.FC<OnboardingPageType> = (props) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;
  return (
    <FileWithLinksForm
      {...props}
      subtitle={name}
      description={CriticalRolePatents}
      title={moduleName}
      fileType={IndividualDocumentType.Patent}
      footnote={FileFootnote}
    />
  );
};

export const InternalDocuments: React.FC<OnboardingPageType> = (props) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;
  return (
    <FileWithLinksForm
      {...props}
      subtitle={name}
      description={CriticalRoleInternalDocuments}
      title={moduleName}
      fileType={IndividualDocumentType.InternalDocs}
      footnote={ScreenshotFootnote}
    />
  );
};

export const MembershipDocuments: React.FC<OnboardingPageType> = (props) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;
  return (
    <FileWithLinksForm
      {...props}
      subtitle={name}
      description=""
      title={moduleName}
      fileType={IndividualDocumentType.MembershipDocumentation}
    />
  );
};

export const JudgingDocumentForm: React.FC<OnboardingPageType> = (props) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;
  return (
    <FileWithLinksForm
      {...props}
      subtitle={name}
      description={
        "Please include link to the event where you participated as a judge and and a screenshot clearly showing the prize details and judging panel."
      }
      title={moduleName}
      fileType={IndividualDocumentType.JudgingDocumentation}
      footnote={FileFootnote}
    />
  );
};

export const PressArticleForm: React.FC<OnboardingPageType> = (props) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;
  return (
    <FileWithLinksForm
      {...props}
      subtitle={name}
      description="An article featuring you and your novel creation in a press publication."
      title={moduleName}
      fileType={IndividualDocumentType.PressArticle}
      footnote={FileFootnote}
    />
  );
};

export const GoogleScholarArticleForm: React.FC<OnboardingPageType> = (
  props
) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;
  return (
    <FileWithLinksForm
      {...props}
      subtitle={name}
      description=""
      title={moduleName}
      fileType={IndividualDocumentType.GoogleScholarPage}
    />
  );
};

export const OriginalContributionEvidenceForm: React.FC<OnboardingPageType> = (
  props
) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;
  return (
    <FileWithLinksForm
      {...props}
      subtitle={name}
      description=""
      title={moduleName}
      fileType={IndividualDocumentType.PublicationProminence}
      footnote={FileFootnote}
    />
  );
};

export const JournalArticleWithLinksForm: React.FC<OnboardingPageType> = (
  props
) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;
  return (
    <FileWithLinksForm
      {...props}
      subtitle={name}
      description={CriticalRoleScholarlyArticle}
      title={moduleName}
      fileType={IndividualDocumentType.JournalArticle}
      footnote={FileFootnote}
    />
  );
};

export const EvidenceOfCitationForm: React.FC<OnboardingPageType> = (props) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;
  return (
    <FileWithLinksForm
      {...props}
      subtitle={name}
      description={
        "Please include any link or screenshots showing your work has been widely cited, referenced, or used by others in your industry."
      }
      title={moduleName}
      fileType={IndividualDocumentType.EvidenceOfCitation}
      footnote={FileFootnote}
    />
  );
};

export const PublicationProminenceForm: React.FC<OnboardingPageType> = (
  props
) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;
  return (
    <FileWithLinksForm
      {...props}
      subtitle={name}
      description="Any other major publications where your work has appeared"
      title={moduleName}
      fileType={IndividualDocumentType.PublicationProminence}
      footnote={FileFootnote}
    />
  );
};
